import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Puzzle, Timer, Trophy, RotateCcw, CheckCircle } from 'lucide-react';

const PuzzleGame = () => {
  const { t } = useTranslation();
  const [puzzle, setPuzzle] = useState([]);
  const [emptyPos, setEmptyPos] = useState(8);
  const [moves, setMoves] = useState(0);
  const [time, setTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [level, setLevel] = useState(1);

  const initializePuzzle = useCallback(() => {
    const size = level <= 2 ? 3 : 4;
    const totalTiles = size * size;
    const tiles = Array.from({ length: totalTiles - 1 }, (_, i) => i + 1);
    tiles.push(0); // Empty tile
    
    // Shuffle tiles
    for (let i = tiles.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [tiles[i], tiles[j]] = [tiles[j], tiles[i]];
    }
    
    setPuzzle(tiles);
    setEmptyPos(tiles.indexOf(0));
    setMoves(0);
    setTime(0);
    setIsPlaying(false);
    setIsCompleted(false);
  }, [level]);

  useEffect(() => {
    initializePuzzle();
  }, [initializePuzzle]);

  useEffect(() => {
    let interval;
    if (isPlaying && !isCompleted) {
      interval = setInterval(() => {
        setTime(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isPlaying, isCompleted]);

  const startGame = () => {
    setIsPlaying(true);
  };

  const canMove = (index) => {
    const size = level <= 2 ? 3 : 4;
    const row = Math.floor(index / size);
    const col = index % size;
    const emptyRow = Math.floor(emptyPos / size);
    const emptyCol = emptyPos % size;
    
    return (
      (Math.abs(row - emptyRow) === 1 && col === emptyCol) ||
      (Math.abs(col - emptyCol) === 1 && row === emptyRow)
    );
  };

  const moveTile = (index) => {
    if (!isPlaying || !canMove(index)) return;
    
    const newPuzzle = [...puzzle];
    [newPuzzle[index], newPuzzle[emptyPos]] = [newPuzzle[emptyPos], newPuzzle[index]];
    
    setPuzzle(newPuzzle);
    setEmptyPos(index);
    setMoves(prev => prev + 1);
    
    // Check if puzzle is completed
    if (checkCompletion(newPuzzle)) {
      setIsCompleted(true);
      setIsPlaying(false);
    }
  };

  const checkCompletion = (puzzleState) => {
    const size = level <= 2 ? 3 : 4;
    const totalTiles = size * size;
    
    for (let i = 0; i < totalTiles - 1; i++) {
      if (puzzleState[i] !== i + 1) return false;
    }
    return puzzleState[totalTiles - 1] === 0;
  };

  const resetGame = () => {
    initializePuzzle();
  };

  const nextLevel = () => {
    if (level < 3) {
      setLevel(prev => prev + 1);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getScore = () => {
    const baseScore = 1000;
    const timePenalty = Math.floor(time / 10) * 10;
    const movePenalty = moves * 5;
    return Math.max(0, baseScore - timePenalty - movePenalty);
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Game Header */}
      <div className="cyber-card p-6 mb-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div className="flex items-center justify-center space-x-2">
            <Puzzle className="h-6 w-6 text-neon-blue" />
            <div>
              <p className="text-sm text-gray-400 font-tech">Moves</p>
              <p className="text-2xl font-cyber font-bold text-neon-blue">{moves}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center space-x-2">
            <Trophy className="h-6 w-6 text-neon-green" />
            <div>
              <p className="text-sm text-gray-400 font-tech">{t('game.level')}</p>
              <p className="text-2xl font-cyber font-bold text-neon-green">{level}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center space-x-2">
            <Timer className="h-6 w-6 text-neon-pink" />
            <div>
              <p className="text-sm text-gray-400 font-tech">{t('game.time')}</p>
              <p className="text-2xl font-cyber font-bold text-neon-pink">{formatTime(time)}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center space-x-2">
            <RotateCcw className="h-6 w-6 text-neon-yellow" />
            <button
              onClick={resetGame}
              className="cyber-button text-sm px-4 py-2"
            >
              {t('puzzle.reset')}
            </button>
          </div>
        </div>
      </div>

      {/* Game Instructions */}
      {!isPlaying && !isCompleted && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="cyber-card p-6 mb-8 text-center"
        >
          <h3 className="text-2xl font-cyber font-bold text-white mb-4">
            {t('puzzle.title')}
          </h3>
          <p className="text-gray-300 font-tech mb-6">
            {t('puzzle.instructions', { count: level <= 2 ? 8 : 15 })}
          </p>
          <button
            onClick={startGame}
            className="cyber-button text-lg px-8 py-3"
          >
            {t('puzzle.startGame')}
          </button>
        </motion.div>
      )}

      {/* Completion Message */}
      {isCompleted && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="cyber-card p-6 mb-8 text-center border-neon-green/50"
        >
          <CheckCircle className="h-16 w-16 text-neon-green mx-auto mb-4" />
          <h3 className="text-3xl font-cyber font-bold text-neon-green mb-4">
            {t('puzzle.completed')}
          </h3>
          <p className="text-gray-300 font-tech mb-4">
            {t('puzzle.score')}: {getScore()} | {t('puzzle.moves')}: {moves} | {t('puzzle.time')}: {formatTime(time)}
          </p>
          <div className="flex justify-center space-x-4">
            {level < 3 && (
              <button
                onClick={nextLevel}
                className="cyber-button text-lg px-6 py-3 border-neon-green text-neon-green hover:bg-neon-green hover:text-dark-300"
              >
                {t('puzzle.nextLevel')}
              </button>
            )}
            <button
              onClick={resetGame}
              className="cyber-button text-lg px-6 py-3"
            >
              {t('puzzle.playAgain')}
            </button>
          </div>
        </motion.div>
      )}

      {/* Game Grid */}
      <div className="cyber-card p-6">
        <div 
          className="grid gap-2 mx-auto"
          style={{
            gridTemplateColumns: `repeat(${level <= 2 ? 3 : 4}, 1fr)`,
            maxWidth: level <= 2 ? '300px' : '400px'
          }}
        >
          <AnimatePresence>
            {puzzle.map((tile, index) => (
              <motion.div
                key={`${tile}-${index}`}
                layout
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                whileHover={{ scale: canMove(index) ? 1.05 : 1 }}
                whileTap={{ scale: canMove(index) ? 0.95 : 1 }}
                className={`
                  aspect-square cursor-pointer rounded-lg border-2 transition-all duration-300 flex items-center justify-center text-2xl font-cyber font-bold
                  ${tile === 0 
                    ? 'bg-transparent border-transparent cursor-default' 
                    : canMove(index)
                      ? 'bg-neon-blue/20 border-neon-blue text-neon-blue hover:bg-neon-blue/30 hover:shadow-[0_0_15px_rgba(0,245,255,0.3)]' 
                      : 'bg-dark-100 border-neon-blue/30 text-gray-400'
                  }
                `}
                onClick={() => moveTile(index)}
              >
                {tile !== 0 && tile}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>

      {/* Game Stats */}
      {isPlaying && (
        <div className="cyber-card p-6 mt-8">
          <div className="text-center">
            <h4 className="text-xl font-cyber font-bold text-white mb-4">
              {t('puzzle.gameStats')}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <p className="text-gray-400 font-tech">{t('puzzle.currentScore')}</p>
                <p className="text-2xl font-cyber font-bold text-neon-blue">{getScore()}</p>
              </div>
              <div className="text-center">
                <p className="text-gray-400 font-tech">{t('puzzle.efficiency')}</p>
                <p className="text-2xl font-cyber font-bold text-neon-green">
                  {moves > 0 ? Math.round((1000 / moves) * 100) : 0}%
                </p>
              </div>
              <div className="text-center">
                <p className="text-gray-400 font-tech">{t('puzzle.timeBonus')}</p>
                <p className="text-2xl font-cyber font-bold text-neon-pink">
                  {Math.max(0, 1000 - Math.floor(time / 10) * 10)}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PuzzleGame;
